import { Controller } from '@hotwired/stimulus';

const MATCH_FIELDS = [
  { key: 'label', label: 'ID' },
  { key: 'email', label: 'E-mail' },
  { key: 'name', label: 'Nazwa' },
  { key: 'task', label: 'Zadanie' },
];

function matchesLabel(count) {
  if (count === 1) return '1 pasujący czat';

  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (lastDigit >= 2 && lastDigit <= 4 && !(lastTwoDigits >= 12 && lastTwoDigits <= 14)) {
    return `${count} pasujące czaty`;
  }

  return `${count} pasujących czatów`;
}

export default class extends Controller {
  static targets = ['input', 'clearButton', 'dropdown', 'resultsLabel', 'results', 'resultTemplate', 'empty'];

  static values = { chats: Array };

  connect() {
    this.activeIndex = -1;
    this.renderedQuery = null;
  }

  open() {
    if (this.dropdownTarget.hidden || this.renderedQuery !== this.inputTarget.value) this.render();

    this.show();
  }

  show() {
    this.dropdownTarget.hidden = false;
    this.inputTarget.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.dropdownTarget.hidden = true;
    this.inputTarget.setAttribute('aria-expanded', 'false');
    this.inputTarget.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  }

  search() {
    this.render();
    this.show();
  }

  clear() {
    this.inputTarget.value = '';
    this.open();
    this.inputTarget.focus();
  }

  focusInput() {
    this.inputTarget.focus();
  }

  closeOnOutside(event) {
    if (this.element.contains(event.target)) return;

    this.close();
  }

  navigate(event) {
    switch (event.key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Enter': {
        const active = this.resultElements[this.activeIndex];
        if (!active) break;

        event.preventDefault();
        active.click();
        break;
      }
      default:
        break;
    }
  }

  get resultElements() {
    return Array.from(this.resultsTarget.children);
  }

  moveActive(step) {
    if (this.dropdownTarget.hidden) this.open();

    const results = this.resultElements;
    if (results.length === 0) return;

    const start = this.activeIndex < 0 && step < 0 ? 0 : this.activeIndex;
    this.activeIndex = (start + step + results.length) % results.length;

    results.forEach((result, index) => {
      result.setAttribute('aria-selected', String(index === this.activeIndex));
    });

    const active = results[this.activeIndex];
    this.inputTarget.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  }

  render() {
    const query = this.inputTarget.value.trim();
    const matches = this.matches(query);

    this.activeIndex = -1;
    this.renderedQuery = this.inputTarget.value;
    this.inputTarget.removeAttribute('aria-activedescendant');
    this.clearButtonTarget.hidden = this.inputTarget.value.length === 0;
    this.resultsLabelTarget.textContent = query
      ? matchesLabel(matches.length)
      : `Wszystkie czaty (${this.chatsValue.length})`;
    this.resultsTarget.replaceChildren(...matches.map((match, index) => this.resultElement(match, query, index)));
    this.emptyTarget.hidden = matches.length > 0;
    this.emptyTarget.textContent = query ? `Brak czatów dla „${query}”` : 'Brak czatów.';
  }

  matches(query) {
    const needle = query.toLowerCase();

    return this.chatsValue.reduce((matched, chat) => {
      const field = this.matchedField(chat, needle);
      if (field) matched.push({ chat, field });

      return matched;
    }, []);
  }

  matchedField(chat, needle) {
    const present = MATCH_FIELDS.filter(({ key }) => Boolean(chat[key]));
    if (!needle) return present[0] || null;

    return present.find(({ key }) => chat[key].toLowerCase().includes(needle)) || null;
  }

  resultElement({ chat, field }, query, index) {
    const link = this.resultTemplateTarget.content.cloneNode(true).querySelector('[data-slot="link"]');
    const title = link.querySelector('[data-slot="title"]');

    link.href = chat.path;
    link.id = `llm_chat_search_option_${index}`;
    link.querySelector('[data-slot="created"]').textContent = `Utworzono: ${chat.created}`;

    if (field.key === 'name') {
      title.replaceChildren(...this.highlight(chat.display, query));
      link.querySelector('[data-slot="match"]').hidden = true;

      return link;
    }

    title.textContent = chat.display;
    link.querySelector('[data-slot="field"]').textContent = field.label;
    link.querySelector('[data-slot="value"]').replaceChildren(...this.highlight(chat[field.key], query));

    return link;
  }

  highlight(text, query) {
    if (!query) return [text];

    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index < 0) return [text];

    const mark = document.createElement('span');
    mark.className = 'llm-chat-search-mark';
    mark.textContent = text.slice(index, index + query.length);

    return [text.slice(0, index), mark, text.slice(index + query.length)];
  }
}
